// [src/utils/tokenManager.jsx]

// 30일 (밀리초)
const TOKEN_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

// 키 상수 정의 (rtoken 추가)
const STORAGE_KEYS = {
    TOKEN: 'atoken',
    REFRESH_TOKEN: 'rtoken', // 리프레시 토큰 키
    EXPIRY: 'tokenExpiry',
    USER_INFO: 'userInfo'
};

const LEGACY_KEYS = ['token'];

export const clearAllAuthStorage = () => {
    // 모든 스토리지 삭제 (rtoken 포함)
    const keys = Object.values(STORAGE_KEYS);
    keys.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
    });

    // 구버전 키 삭제
    LEGACY_KEYS.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
    });

    // 플러터(Native)에게도 삭제 요청
    if (window.flutter_inappwebview && window.flutter_inappwebview.callHandler) {
        window.flutter_inappwebview.callHandler('NativeLogout');
    }
};

/**
 * 토큰 상태 확인 및 스토리지 자가 복구(Self-Healing)
 * - Access Token 뿐만 아니라 Refresh Token도 동기화합니다.
 */
export const getTokenStatus = () => {
    // 각 스토리지에서 데이터 가져오기
    const sessionToken = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    const sessionRToken = sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN); //
    const sessionInfo = sessionStorage.getItem(STORAGE_KEYS.USER_INFO);

    const localToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const localRToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN); //
    const localInfo = localStorage.getItem(STORAGE_KEYS.USER_INFO);

    let finalToken = null;
    let finalRToken = null; //
    let finalUserInfoStr = null;

    // 데이터 복구 로직 (Session <-> Local 동기화)
    // 우선순위: Session에 데이터가 있으면 그것을 기준, 없으면 Local을 기준으로 복구

    if (sessionToken && !localToken) {
        // [복구] 세션 -> 로컬
        localStorage.setItem(STORAGE_KEYS.TOKEN, sessionToken);
        if (sessionRToken) localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, sessionRToken);
        if (sessionInfo) localStorage.setItem(STORAGE_KEYS.USER_INFO, sessionInfo);

        finalToken = sessionToken;
        finalRToken = sessionRToken;
        finalUserInfoStr = sessionInfo;
    } else if (!sessionToken && localToken) {
        // [복구] 로컬 -> 세션
        sessionStorage.setItem(STORAGE_KEYS.TOKEN, localToken);
        if (localRToken) sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, localRToken);
        if (localInfo) sessionStorage.setItem(STORAGE_KEYS.USER_INFO, localInfo);

        finalToken = localToken;
        finalRToken = localRToken;
        finalUserInfoStr = localInfo;
    } else {
        // 둘 다 존재하거나 둘 다 없는 경우
        finalToken = sessionToken || localToken;
        finalRToken = sessionRToken || localRToken;
        finalUserInfoStr = sessionInfo || localInfo;
    }

    // 만료 체크 (Access Token 기준)
    let isExpired = !finalToken;

    // UserInfo 파싱 및 검증
    let userInfo = null;
    try {
        if (finalUserInfoStr && finalUserInfoStr !== "undefined" && finalUserInfoStr !== "null") {
            userInfo = JSON.parse(finalUserInfoStr);
            // [수정] typeof 체크 추가: 과거 인자 순서 버그로 userInfo 자리에 true(불리언)가
            // 저장된 경우 JSON.parse("true")는 에러 없이 true를 반환하므로 객체 형태인지 확인 필요.
            if (!userInfo || typeof userInfo !== 'object' || !userInfo.userId || userInfo.userId === "null") {
                userInfo = null;
                // [자가치유] 오염된 캐시를 즉시 제거하여 잔여 쓰레기 값이 남지 않도록 함 (2026-06-24)
                localStorage.removeItem(STORAGE_KEYS.USER_INFO);
                sessionStorage.removeItem(STORAGE_KEYS.USER_INFO);
            }
        }
    } catch (e) {
        console.error("User info parse error", e);
        userInfo = null;
        localStorage.removeItem(STORAGE_KEYS.USER_INFO);
        sessionStorage.removeItem(STORAGE_KEYS.USER_INFO);
    }

    // [반환값 추가] rtoken도 반환하여 API 인터셉터 등에서 사용할 수 있게 함
    return { token: finalToken, rtoken: finalRToken, isExpired, userInfo };
};

// rtoken 파라미터 추가
export const updateTokenAndExpiry = (atoken, rtoken, userInfo, isAutoLogin) => {
    const userInfoString = JSON.stringify(userInfo);
    const expiryTime = new Date().getTime() + TOKEN_DURATION_MS;
    const expiryTimeString = String(expiryTime);

    // 로컬 스토리지 저장 (자동로그인 체크 시 혹은 이미 로컬에 있을 시)
    if (isAutoLogin || localStorage.getItem(STORAGE_KEYS.TOKEN)) {
        localStorage.setItem(STORAGE_KEYS.TOKEN, atoken);
        if (rtoken) localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, rtoken); //
        localStorage.setItem(STORAGE_KEYS.USER_INFO, userInfoString);
        localStorage.setItem(STORAGE_KEYS.EXPIRY, expiryTimeString);
    }

    // 세션 스토리지 저장 (항상 저장)
    sessionStorage.setItem(STORAGE_KEYS.TOKEN, atoken);
    if (rtoken) sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, rtoken); //
    sessionStorage.setItem(STORAGE_KEYS.USER_INFO, userInfoString);
    sessionStorage.setItem(STORAGE_KEYS.EXPIRY, expiryTimeString);

    // 플러터 동기화
    // [중요] Flutter의 NativeLogin 핸들러도 인자 4개를 받도록 수정되어야 합니다.
    if (window.flutter_inappwebview && window.flutter_inappwebview.callHandler) {
        window.flutter_inappwebview.callHandler(
            'NativeLogin',
            atoken,
            rtoken || "", // rtoken 전달 (없으면 빈 문자열)
            userInfoString,
            expiryTimeString
        );
    }
};

export const resetTokenExpiry = () => {
    const { token, isExpired } = getTokenStatus();
    // const isAutoLogin = !!(localStorage.getItem(STORAGE_KEYS.TOKEN) || localStorage.getItem('token'));
    // 위 로직 대신 명확하게 키 존재 여부로 확인
    const isAutoLogin = localStorage.getItem(STORAGE_KEYS.TOKEN) !== null;

    if (isExpired || !token) return false;

    const newExpiryTime = new Date().getTime() + TOKEN_DURATION_MS;
    const newExpiryTimeString = String(newExpiryTime);

    if (isAutoLogin) {
        localStorage.setItem(STORAGE_KEYS.EXPIRY, newExpiryTimeString);
    }
    sessionStorage.setItem(STORAGE_KEYS.EXPIRY, newExpiryTimeString);

    return true;
};

export const checkTokenIntegrity = () => {
    const localToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const sessionToken = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    // 엄격한 일치 확인
    const isConsistent = localToken && sessionToken && (localToken === sessionToken);
    return { isConsistent, localToken };
};